import React, { memo } from 'react';
import { InterPoint } from '@saviynt/common';
import {
  Box,
  ButtonIcon,
  Chip,
  DateAndTimePicker,
  Icon,
  InlineMessage,
  InputField,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';
import { addYears, format, isValid } from 'date-fns';

import {
  CF_NEU_BLD,
  CF_NEU_INV,
  CF_NEU_STL,
  CF_SEC_MED,
  MIN_COMMENT_LENGTH,
} from '../../../../../shared/constants';
import { useInvertedLocalize } from '../../../../../shared/hooks';

import { type TrackerState } from './ViolationsMitigate';

import './ViolationsMitigateListItem.css';

interface ViolationsMitigateListItemState {
  isRecommended: boolean;
  mitigatingControl: string;
  priority: string;
  expiryDate: string;
  controlType: string;
  tracker: TrackerState;
  description: string | null;
  globalDateFormat: string;
  handleSelection: (id: string) => void;
  handleStartDate: (id: string, startDate: Date | null | undefined) => void;
  handleEndDate: (id: string, startDate: Date | null | undefined) => void;
  handleComment: (id: string, comment: string) => void;
}

/**
 * ViolationsMitigate
 */
function ViolationsMitigateListItem({
  isRecommended,
  mitigatingControl,
  priority,
  expiryDate,
  controlType,
  description,
  tracker,
  globalDateFormat,
  handleSelection,
  handleStartDate,
  handleEndDate,
  handleComment,
}: ViolationsMitigateListItemState) {
  const l = useInvertedLocalize();

  const minDate = new Date();
  const rangeMaxDate = addYears(new Date(), 999);

  const getPriorityChipColor = () => {
    switch (priority) {
      case 'CRITICAL':
        return 'critical';
      case 'HIGH':
        return 'critical';
      case 'MEDIUM':
        return 'warning';
      case 'LOW':
        return 'success';
      default:
        return 'info';
    }
  };

  const wrapperClasses = cx(
    'ViolationsMitigateListItem-wrapper',
    tracker.isChecked && 'ViolationsMitigateListItem-wrapper--isChecked'
  );

  return (
    <li className={wrapperClasses}>
      <Box tag='div' className='ViolationsMitigateListItem-mainContent'>
        {isRecommended && (
          <Box tag='div' className='ViolationsMitigateListItem-starWrapper'>
            <Icon kind='StarFilled' size='smallMedium' color={CF_NEU_INV} />
          </Box>
        )}
        <Box
          tag='div'
          className='ViolationsMitigateListItem-mainContentSection'>
          <Typography kind='h3' color={CF_NEU_BLD}>
            {mitigatingControl}
          </Typography>
          <Box
            tag='div'
            className='ViolationsMitigateListItem-mainContentSectionBody'>
            <Typography kind='body2' color={CF_NEU_STL}>
              {l('Priority:')}
            </Typography>
            <Chip
              kind='subtleFilled'
              size='xSmall'
              color={getPriorityChipColor()}
              label={
                priority.charAt(0).toUpperCase() +
                priority.slice(1).toLowerCase()
              }
              isInteractionDisabled
            />
            <InterPoint />
            <Typography kind='body2' color={CF_NEU_STL}>
              {l('Type:')}
            </Typography>
            <Typography kind='body2' color={CF_NEU_BLD}>
              {controlType}
            </Typography>
            <InterPoint />
            <Typography kind='body2' color={CF_NEU_STL}>
              {l('Expires:')}
            </Typography>
            <Typography kind='body2' color={CF_NEU_BLD}>
              {isValid(new Date(expiryDate))
                ? format(new Date(expiryDate), globalDateFormat)
                : expiryDate}
            </Typography>
            {description && (
              <>
                <InterPoint />
                <Typography kind='body2' color={CF_NEU_STL}>
                  {description}
                </Typography>
              </>
            )}
          </Box>
        </Box>
        <Box
          tag='div'
          className='ViolationsMitigateListItem-mainContentControl'>
          {tracker.isChecked && (
            <Typography kind='label' color={CF_SEC_MED}>
              {l('Selected')}
            </Typography>
          )}
          <ButtonIcon
            size='large'
            kind='outlinedSecondary'
            isSelected={tracker.isChecked}
            icon={
              <Icon
                kind={tracker.isChecked ? 'Subtract' : 'Add'}
                size='xSmall'
              />
            }
            ariaLabel={tracker.isChecked ? l('unselect') : l('select')}
            onClick={() => handleSelection(tracker.id)}
          />
        </Box>
      </Box>
      {tracker.isChecked && (
        <Box tag='div' className='ViolationsMitigateListItem-extendedContent'>
          <Box
            tag='div'
            className='ViolationsMitigateListItem-extendedContent-dateSection'>
            <Box
              tag='div'
              className='ViolationsMitigateListItem-extendedContent-dateWrapper'>
              <DateAndTimePicker
                kind='dateAndTimeStart'
                label={l('Start Date')}
                startDate={tracker.startDate}
                setStartDate={(date) => {
                  handleStartDate(tracker.id, date as Date | null | undefined);
                }}
                endDate={tracker.endDate}
                maxDate={rangeMaxDate}
                minDate={minDate}
                dateFormat='MM/dd/yyyy, h:mm aa'
                isCritical={tracker.isStartDateCritical}
                portalId='root'
                isRequired
                CriticalHelperText={
                  <InlineMessage
                    text={l('This required field is incomplete')}
                    colorTheme='critical'
                    size='small'
                    leftIcon={<Icon kind='AlertCriticalFilled' />}
                  />
                }
              />
            </Box>
            <Box
              tag='div'
              className='ViolationsMitigateListItem-extendedContent-dateWrapper'>
              <DateAndTimePicker
                kind='dateAndTimeEnd'
                label={l('End Date')}
                startDate={tracker.startDate}
                setStartDate={(date) => {
                  handleStartDate(tracker.id, date as Date | null | undefined);
                }}
                endDate={tracker.endDate}
                setEndDate={(date) => {
                  handleEndDate(tracker.id, date as Date | null | undefined);
                }}
                maxDate={rangeMaxDate}
                minDate={minDate}
                dateFormat='MM/dd/yyyy, h:mm aa'
                isCritical={tracker.isEndDateCritical}
                portalId='root'
                isRequired
                CriticalHelperText={
                  <InlineMessage
                    text={l('This required field is incomplete')}
                    colorTheme='critical'
                    size='small'
                    leftIcon={<Icon kind='AlertCriticalFilled' />}
                  />
                }
              />
            </Box>
          </Box>
          <InputField
            name='basic'
            placeholder={l('Provide a reason for your decision')}
            label={
              <Typography kind='h4' color={CF_NEU_BLD}>
                {l('Comments')}
              </Typography>
            }
            value={tracker.comment}
            setValue={(text: string) => handleComment(tracker.id, text)}
            kind='multiline'
            isRequired
            isCritical={tracker.isCommentCritical}
            CriticalHelperText={
              <InlineMessage
                text={
                  tracker.comment.trim().length < 1
                    ? l('This required field is incomplete')
                    : `Your comments must contain more than ${
                        MIN_COMMENT_LENGTH - 1
                      } characters.`
                }
                colorTheme='critical'
                size='small'
                leftIcon={<Icon kind='AlertCriticalFilled' />}
              />
            }
          />
        </Box>
      )}
    </li>
  );
}

export default memo(ViolationsMitigateListItem);
